import Button from '@rio-cloud/rio-uikit/Button';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import TableRowActionsDropdown from '@rio-cloud/rio-uikit/TableRowActionsDropdown';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { getIsLoading } from '../../api/redux/api.redux';
import { ApiCalls } from '../../api/redux/types';
import { DescriptionOfServiceContainer } from '../../common/descriptionOfService/DescriptionOfServiceContainer';
import { SUBSCRIPTION_WRITE } from '../../common/permissions/permissions';
import { getPermissions } from '../../common/permissions/redux/permissions.redux';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import type { TermedLevel } from '../../common/termedLevel/types';
import { TermsAndConditionsContainer } from '../../common/termsAndConditions/TermsAndConditionsContainer';
import { OverlayTrigger, Tooltip } from '../../common/utils/tooltipReExports';
import { DEFAULT_TOOLTIP_DELAY } from '../../config/config';
import { isManNowProduct, MAN_NOW_PARENT_PRODUCT_ID } from '../../config/manNowProducts';
import { ContractTableTrackingAction, getContractTableAutoTrackingKey } from './contractTracking';
import { terminateContractNowThunk } from './terminateContractNow.thunk';

const ContractDocument = {
    TERMS_AND_CONDITIONS: 'TERMS_AND_CONDITIONS',
    DESCRIPTION_OF_SERVICE: 'DESCRIPTION_OF_SERVICE',
} as const;

type ContractDocument = (typeof ContractDocument)[keyof typeof ContractDocument];

interface Props {
    contract: TermedLevel;
    onClick: (e: React.MouseEvent) => void;
    serviceName: string;
    hasPermission: boolean;
    isTerminating: boolean;
    terminateNow: (contract: TermedLevel) => void;
}

function ContractTableRowActionsCell({
    contract,
    onClick,
    serviceName,
    hasPermission,
    isTerminating,
    terminateNow,
}: Props) {
    const [selectedDocument, setSelectedDocument] = useState<ContractDocument>();
    const [contractToTerminate, setContractToTerminate] = useState<TermedLevel>();
    const intl = useIntl();
    const isRenewalEnabled = isTermedLevelSelectable(contract) && hasPermission;
    const tosVersion = contract.tosVersion?.trim() || undefined;
    const dosVersion = contract.dosVersion?.trim() || undefined;
    const hasAnyDocumentVersion = Boolean(tosVersion || dosVersion);
    const usesManNowDocumentFallback = !hasAnyDocumentVersion && isManNowProduct(contract.productId);
    const documentProductId = usesManNowDocumentFallback ? MAN_NOW_PARENT_PRODUCT_ID : contract.productId;
    const canOpenTos = usesManNowDocumentFallback || Boolean(tosVersion);
    const canOpenDos = usesManNowDocumentFallback || Boolean(dosVersion);
    const closeDocument = () => setSelectedDocument(undefined);
    const closeTerminationDialog = () => setContractToTerminate(undefined);
    const renewalButton = (
        <Button
            className='btn btn-default btn-sm btn-icon-only'
            onClick={isRenewalEnabled ? onClick : undefined}
            disabled={!isRenewalEnabled}
            autoTrackingKey={getContractTableAutoTrackingKey(ContractTableTrackingAction.configureRenewal, serviceName)}
            type='button'
            data-onboarding-contracts-walkthrough-step='contract-view-renew-single-button'
        >
            <span className='rioglyph rioglyph-pencil' aria-hidden={true} />
        </Button>
    );

    return (
        <>
            {canOpenTos && (
                <TermsAndConditionsContainer
                    serviceSku={documentProductId}
                    level={contract.level}
                    documentVersion={tosVersion}
                    show={selectedDocument === ContractDocument.TERMS_AND_CONDITIONS}
                    onClose={closeDocument}
                />
            )}
            {canOpenDos && (
                <DescriptionOfServiceContainer
                    serviceSku={documentProductId}
                    level={contract.level}
                    documentVersion={dosVersion}
                    show={selectedDocument === ContractDocument.DESCRIPTION_OF_SERVICE}
                    onClose={closeDocument}
                />
            )}
            {contractToTerminate && (
                <div onClick={event => event.stopPropagation()}>
                    <ConfirmationDialog
                        show={true}
                        title={<FormattedMessage id='marketplace.contracts.terminateNow.confirmation.title' />}
                        content={
                            <FormattedMessage
                                id='marketplace.contracts.terminateNow.confirmation.content'
                                values={{ serviceName: contractToTerminate.productName }}
                            />
                        }
                        onClickConfirm={() => {
                            terminateNow(contractToTerminate);
                            closeTerminationDialog();
                        }}
                        onClickCancel={closeTerminationDialog}
                        cancelButtonText={
                            <FormattedMessage id='marketplace.contracts.terminateNow.confirmation.abort' />
                        }
                        confirmButtonText={
                            <FormattedMessage id='marketplace.contracts.terminateNow.confirmation.confirm' />
                        }
                    />
                </div>
            )}
            <span className='display-flex align-items-center justify-content-end gap-10'>
                {hasPermission &&
                    (isRenewalEnabled ? (
                        renewalButton
                    ) : (
                        <OverlayTrigger
                            trigger={OverlayTrigger.TRIGGER_HOVER}
                            placement='top'
                            overlay={
                                <Tooltip id='contract-renewal-disabled-tooltip' className={'width-auto max-width-300'}>
                                    <div style={{ whiteSpace: 'pre-line' }}>
                                        <FormattedMessage id='marketplace.contracts.renewal.disabled.tooltip' />
                                    </div>
                                </Tooltip>
                            }
                            delay={DEFAULT_TOOLTIP_DELAY}
                        >
                            <span className='d-inline-block'>{renewalButton}</span>
                        </OverlayTrigger>
                    ))}
                <span onClick={event => event.stopPropagation()}>
                    <TableRowActionsDropdown
                        aria-label={intl.formatMessage({ id: 'intl-msg:common-message.contracts.actions' })}
                        items={[
                            {
                                value: (
                                    <span className='display-flex justify-items-center'>
                                        <span
                                            className='rioglyph rioglyph-document margin-right-5'
                                            aria-hidden='true'
                                        />
                                        <FormattedMessage id='marketplace.contracts.actions.showTos' />
                                    </span>
                                ),
                                disabled: !canOpenTos,
                                onSelect: (_value, event) => {
                                    event.stopPropagation();
                                    setSelectedDocument(ContractDocument.TERMS_AND_CONDITIONS);
                                },
                            },
                            {
                                value: (
                                    <span className='display-flex justify-items-center'>
                                        <span
                                            className='rioglyph rioglyph-detail-view-info margin-right-5'
                                            aria-hidden='true'
                                        />
                                        <FormattedMessage id='marketplace.contracts.actions.showDos' />
                                    </span>
                                ),
                                disabled: !canOpenDos,
                                onSelect: (_value, event) => {
                                    event.stopPropagation();
                                    setSelectedDocument(ContractDocument.DESCRIPTION_OF_SERVICE);
                                },
                            },
                            ...(contract.isCancellable && hasPermission
                                ? [
                                      {
                                          value: (
                                              <span className='display-flex justify-items-center'>
                                                  <span
                                                      className='rioglyph rioglyph-remove-circle margin-right-5'
                                                      aria-hidden='true'
                                                  />
                                                  <FormattedMessage id='marketplace.contracts.terminateNow' />
                                              </span>
                                          ),
                                          disabled: isTerminating,
                                          onSelect: (
                                              _value: number | undefined,
                                              event: React.MouseEvent<HTMLLIElement>
                                          ) => {
                                              event.stopPropagation();
                                              setContractToTerminate(contract);
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </span>
            </span>
        </>
    );
}

function makeMapStateToProps() {
    return (state: RootState) => ({
        hasPermission: getPermissions(state)?.includes(SUBSCRIPTION_WRITE) ?? false,
        isTerminating: getIsLoading(state, ApiCalls.SUBSCRIPTION_CANCEL),
    });
}

const mapDispatchToProps = (dispatch: RootDispatch) => ({
    terminateNow: (contract: TermedLevel) => dispatch(terminateContractNowThunk(contract)),
});

export const ContractTableRowActionsCellContainer = connect(
    makeMapStateToProps,
    mapDispatchToProps
)(ContractTableRowActionsCell);
